import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { PhotoCamera, Delete as DeleteIcon } from '@mui/icons-material';
import { cpeService } from '../services/cpeService';
import zoneService from '../services/zoneService';
import nextActionOptionService from '../services/nextActionOptionService';
import { userService } from '../services/userService';
import {
  CPEFormData,
  PLANT_TYPES,
  CPE_STATUSES,
  COMMERCIAL_STATUSES,
  PlantType,
  InternalPositioning,
  CPEStatus,
  CommercialStatus,
  NextActionOption,
} from '../types/cpe';
import { User } from '../types/user';

interface ZoneOption {
  id: string;
  name: string;
  color: string;
}

export const CPEFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [currentUpdatedAt, setCurrentUpdatedAt] = useState<string>('');
  const [zones, setZones] = useState<ZoneOption[]>([]);
  const [nextActionOptions, setNextActionOptions] = useState<NextActionOption[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  // Get coordinates from location state if coming from map
  const locationState = location.state as { latitude?: string; longitude?: string; from?: string } | null;

  const [formData, setFormData] = useState<CPEFormData>({
    name: '',
    latitude: locationState?.latitude || '',
    longitude: locationState?.longitude || '',
    plant_type: [],
    internal_positioning: null,
    cpe_status: null,
    power: '',
    short_description: '',
    notes: '',
    company_cui: '',
    epc: '',
    dispatcher: '',
    maintenance_company: '',
    project_link: '',
    internal_link: '',
    exact_location: true,
    is_waldevar_pp: false,
    commercial_status: null,
    om_account_manager: '',
    energization_year: '',
    zone_id: null,
    next_action_id: null,
    next_action_due_date: null,
  });

  const loadCPE = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const cpe = await cpeService.getCPEById(id);
      setFormData({
        name: cpe.name,
        latitude: cpe.latitude,
        longitude: cpe.longitude,
        plant_type: cpe.plant_type,
        internal_positioning: cpe.internal_positioning,
        cpe_status: cpe.cpe_status,
        power: cpe.power || '',
        short_description: cpe.short_description || '',
        notes: cpe.notes || '',
        company_cui: cpe.company_cui || '',
        epc: cpe.epc || '',
        dispatcher: cpe.dispatcher || '',
        maintenance_company: cpe.maintenance_company || '',
        project_link: cpe.project_link || '',
        internal_link: cpe.internal_link || '',
        exact_location: cpe.exact_location ?? true,
        is_waldevar_pp: cpe.is_waldevar_pp ?? false,
        commercial_status: cpe.commercial_status,
        om_account_manager: cpe.om_account_manager || '',
        energization_year: cpe.energization_year || '',
        zone_id: cpe.zone_id || null,
        next_action_id: cpe.next_action_id || null,
        next_action_due_date: cpe.next_action_due_date || null,
      });
      setCurrentUpdatedAt(cpe.updated_at.toString());
      if (cpe.image_url) {
        setImagePreview(cpeService.getImageUrl(cpe.image_url));
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      setError(error.response?.data?.message || 'Failed to load CPE');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit && id) {
      loadCPE();
    }
  }, [id, isEdit, loadCPE]);

  useEffect(() => {
    loadZones();
    loadUsers();
    loadNextActionOptions();
  }, []);

  const loadZones = async () => {
    try {
      const zonesList = await zoneService.getZonesList();
      setZones(zonesList.map(z => ({ id: z.id, name: z.name, color: z.color })));
    } catch (error) {
      console.error('Error loading zones:', error);
    }
  };

  const loadNextActionOptions = async () => {
    try {
      const opts = await nextActionOptionService.getAll();
      setNextActionOptions(opts);
    } catch (error) {
      console.error('Error loading next action options:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const usersList = await userService.getAllUsers();
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleChange = (field: keyof CPEFormData, value: string | number | boolean | null | PlantType[] | InternalPositioning | CPEStatus | CommercialStatus) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Image size must be less than 10MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    // Validation
    const errors: Record<string, string> = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Name is required';
    }

    if (!formData.latitude || formData.latitude === '') {
      errors.latitude = 'Latitude is required';
    } else {
      const lat = parseFloat(formData.latitude.toString());
      if (isNaN(lat) || lat < -90 || lat > 90) {
        errors.latitude = 'Latitude must be between -90 and 90';
      }
    }

    if (!formData.longitude || formData.longitude === '') {
      errors.longitude = 'Longitude is required';
    } else {
      const lng = parseFloat(formData.longitude.toString());
      if (isNaN(lng) || lng < -180 || lng > 180) {
        errors.longitude = 'Longitude must be between -180 and 180';
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fix the errors above');
      return;
    }

    try {
      setSaving(true);

      // Prepare data
      const data: CPEFormData & { latitude: number; longitude: number; power: number | null; energization_year: number | null } = {
        ...formData,
        latitude: parseFloat(formData.latitude.toString()),
        longitude: parseFloat(formData.longitude.toString()),
        power: formData.power ? parseFloat(formData.power.toString()) : null,
        energization_year: formData.energization_year
          ? parseInt(formData.energization_year.toString(), 10)
          : null,
      };

      let cpeId: string;

      if (isEdit && id) {
        // Update
        const updated = await cpeService.updateCPE(id, data, currentUpdatedAt);
        cpeId = updated.id;
      } else {
        // Create
        const created = await cpeService.createCPE(data);
        cpeId = created.id;
      }

      // Upload image if selected
      if (imageFile) {
        await cpeService.uploadCPEImage(cpeId, imageFile);
      }

      navigate(`/cpes/${cpeId}`);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        setFieldErrors(error.response.data.errors);
        setError('Please fix the validation errors below');
      } else if (error.response?.status === 409) {
        if (error.response?.data?.message?.includes('coordinates')) {
          setFieldErrors({
            latitude: 'These coordinates are already in use',
            longitude: 'These coordinates are already in use',
          });
          setError('A CPE already exists at these coordinates');
        } else {
          setError(
            'This CPE has been modified by another user. Please refresh and try again.'
          );
        }
      } else {
        setError(error.response?.data?.message || 'Failed to save CPE');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Use browser history to go back to previous page
    // This works whether user came from map, dashboard, or CPE list
    navigate(-1);
  };

  // Scroll to top when error appears
  useEffect(() => {
    if (error) {
      document.getElementById('main-content')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>
          {isEdit ? t('cpe.editCpe') : t('cpe.createNew')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('cpe.basicInformation')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={t('cpe.fields.name')}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('cpe.fields.companyCui')}
                  value={formData.company_cui}
                  onChange={(e) => handleChange('company_cui', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={t('cpe.fields.latitude')}
                  type="number"
                  inputProps={{ step: '0.000001' }}
                  value={formData.latitude}
                  onChange={(e) => handleChange('latitude', e.target.value)}
                  error={!!fieldErrors.latitude}
                  helperText={fieldErrors.latitude}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={t('cpe.fields.longitude')}
                  type="number"
                  inputProps={{ step: '0.000001' }}
                  value={formData.longitude}
                  onChange={(e) => handleChange('longitude', e.target.value)}
                  error={!!fieldErrors.longitude}
                  helperText={fieldErrors.longitude}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('cpe.fields.plantType')}</InputLabel>
                  <Select
                    multiple
                    value={formData.plant_type}
                    onChange={(e) => handleChange('plant_type', e.target.value)}
                    input={<OutlinedInput label={t('cpe.fields.plantType')} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as PlantType[]).map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {PLANT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('cpe.fields.power')}
                  type="number"
                  value={formData.power}
                  onChange={(e) => handleChange('power', e.target.value)}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {t('cpe.status')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('cpe.fields.cpeStatus')}</InputLabel>
                  <Select
                    value={formData.cpe_status || ''}
                    onChange={(e) => handleChange('cpe_status', e.target.value || null)}
                    label={t('cpe.fields.cpeStatus')}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {CPE_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('cpe.fields.commercialStatus')}</InputLabel>
                  <Select
                    value={formData.commercial_status || ''}
                    onChange={(e) => handleChange('commercial_status', e.target.value || null)}
                    label={t('cpe.fields.commercialStatus')}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {COMMERCIAL_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('cpe.fields.internalPositioning')}</InputLabel>
                  <Select
                    value={formData.internal_positioning || ''}
                    onChange={(e) => handleChange('internal_positioning', e.target.value || null)}
                    label={t('cpe.fields.internalPositioning')}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    <MenuItem value="go">{t('cpe.positioning.go')}</MenuItem>
                    <MenuItem value="no_go">{t('cpe.positioning.noGo')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('cpe.fields.energizationYear')}
                  type="number"
                  value={formData.energization_year}
                  onChange={(e) => handleChange('energization_year', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('cpe.fields.zone')}</InputLabel>
                  <Select
                    value={formData.zone_id || ''}
                    onChange={(e) => handleChange('zone_id', e.target.value || null)}
                    label={t('cpe.fields.zone')}
                  >
                    <MenuItem value="">{t('common.none')}</MenuItem>
                    {zones.map((zone) => (
                      <MenuItem key={zone.id} value={zone.id}>
                        {zone.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Next Action */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel shrink>{t('nextAction.label', 'Next Action')}</InputLabel>
                  <Select
                    value={formData.next_action_id || ''}
                    onChange={(e) => {
                      const val = e.target.value || null;
                      handleChange('next_action_id', val);
                      if (!val) handleChange('next_action_due_date', null);
                    }}
                    displayEmpty
                    input={<OutlinedInput label={t('nextAction.label', 'Next Action')} notched />}
                  >
                    <MenuItem value="">
                      <em>{t('nextAction.noAction', 'No action set')}</em>
                    </MenuItem>
                    {nextActionOptions
                      .filter((o) => o.is_active || o.id === formData.next_action_id)
                      .map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>
                          {opt.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Next Action Due Date */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('nextAction.dueDate', 'Next Action Due Date')}
                  type="date"
                  value={formData.next_action_due_date || ''}
                  onChange={(e) => handleChange('next_action_due_date', e.target.value || null)}
                  disabled={!formData.next_action_id}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Additional Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {t('cpe.additionalDetails')}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('cpe.fields.epc')}
                  value={formData.epc}
                  onChange={(e) => handleChange('epc', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('cpe.fields.dispatcher')}
                  value={formData.dispatcher}
                  onChange={(e) => handleChange('dispatcher', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('cpe.fields.maintenanceCompany')}
                  value={formData.maintenance_company}
                  onChange={(e) => handleChange('maintenance_company', e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel shrink>{t('cpe.fields.omAccountManager')}</InputLabel>
                  <Select
                    value={formData.om_account_manager || ''}
                    onChange={(e) => handleChange('om_account_manager', e.target.value || null)}
                    displayEmpty
                    input={<OutlinedInput label={t('cpe.fields.omAccountManager')} notched />}
                  >
                    <MenuItem value="">
                      <em>{t('common.none')}</em>
                    </MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name || user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Exact Location Checkbox */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.exact_location}
                      onChange={(e) => handleChange('exact_location', e.target.checked)}
                    />
                  }
                  label={t('cpe.fields.exactLocation', 'Exact Location')}
                  sx={{ height: '56px', display: 'flex', alignItems: 'center' }}
                />
              </Grid>

              {/* Waldevar PP Checkbox */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_waldevar_pp}
                      onChange={(e) => handleChange('is_waldevar_pp', e.target.checked)}
                    />
                  }
                  label={t('cpe.fields.isWaldevarPP', 'Waldevar PP')}
                  sx={{ height: '56px', display: 'flex', alignItems: 'center' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('cpe.fields.internalLink', 'Internal Link')}
                  value={formData.internal_link}
                  onChange={(e) => handleChange('internal_link', e.target.value)}
                  helperText={t('cpe.hints.internalLink', 'SharePoint/Teams link')}
                  placeholder="https://waldevar.sharepoint.com/..."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('cpe.fields.externalLink', 'External Link')}
                  value={formData.project_link}
                  onChange={(e) => handleChange('project_link', e.target.value)}
                  placeholder="https://..."
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('cpe.fields.shortDescription')}
                  multiline
                  rows={2}
                  value={formData.short_description}
                  onChange={(e) => handleChange('short_description', e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('cpe.fields.notes')}
                  multiline
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                />
              </Grid>

              {/* Image Upload */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {t('cpe.image')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {imagePreview && (
                    <Avatar
                      src={imagePreview}
                      variant="rounded"
                      sx={{ width: 120, height: 120 }}
                    />
                  )}
                  <Box>
                    <input
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      id="image-upload"
                      type="file"
                      onChange={handleImageSelect}
                    />
                    <label htmlFor="image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCamera />}
                      >
                        {t('common.upload')}
                      </Button>
                    </label>
                    {imagePreview && (
                      <IconButton
                        color="error"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview(null);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {t('cpe.imageInfo', 'Max size: 10MB. Formats: JPG, PNG, WEBP. Will be resized to 512x512 max.')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={saving}
                  >
                    {saving ? t('common.loading') : isEdit ? t('common.save') : t('cpe.createNew')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    {t('common.cancel')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};
